export type TemplateWebMCPErrorCode =
  | 'EDITOR_NOT_READY'
  | 'INVALID_INPUT'
  | 'UNAUTHORIZED'
  | 'UNEXPECTED';

export type TemplateWebMCPSuccess<T> = {
  ok: true;
  data: T;
};

export type TemplateWebMCPFailure = {
  ok: false;
  error: {
    code: TemplateWebMCPErrorCode;
    message: string;
    details?: unknown;
  };
};

export type TemplateWebMCPPayload<T> = TemplateWebMCPSuccess<T> | TemplateWebMCPFailure;

export type TemplateWebMCPToolResult<T> = {
  content: Array<{ type: 'text'; text: string }>;
  structuredContent: TemplateWebMCPPayload<T>;
  isError?: true;
};

export function toolSuccess<T>(data: T): TemplateWebMCPSuccess<T> {
  return { ok: true, data };
}

export function toolError(
  code: TemplateWebMCPErrorCode,
  message: string,
  details?: unknown,
): TemplateWebMCPFailure {
  return {
    ok: false,
    error: details === undefined ? { code, message } : { code, message, details },
  };
}

function humanReadable<T>(payload: TemplateWebMCPPayload<T>): string {
  if (!payload.ok) {
    return payload.error.message;
  }

  const data = payload.data as { templateId?: unknown };
  if (typeof data?.templateId === 'string' && data.templateId.length > 0) {
    return `Read template ${data.templateId} successfully.`;
  }

  return 'Tool completed successfully.';
}

export function mcpToolResult<T>(payload: TemplateWebMCPPayload<T>): TemplateWebMCPToolResult<T> {
  // Harnesses that forward only `content` to the model never see `structuredContent`,
  // so the payload is serialized here as well. See MCP tools spec, structured content.
  const content = [
    { type: 'text' as const, text: humanReadable(payload) },
    { type: 'text' as const, text: JSON.stringify(payload) },
  ];
  if (payload.ok) {
    return { content, structuredContent: payload };
  }
  return { content, structuredContent: payload, isError: true };
}
